import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import dayjs from 'dayjs'
import html2canvas from 'html2canvas'
import {
    Download,
    ImageDown,
    Minus,
    TrendingDown,
    TrendingUp,
} from 'lucide-react'
import { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Loading, Typogaphy } from 'vnetwork-ui'
import * as XLSX from 'xlsx'
import { getConversionFunnel } from '../services'
import { CustomerPipelineFilters } from '../types'

interface ConversionFunnelChartProps {
    filters: CustomerPipelineFilters
    enabledConversionRates?: boolean
}

// Curved Funnel Layer Component - Hourglass style
interface CurvedFunnelLayerProps {
    topWidth: number
    bottomWidth: number
    height: number
    y: number
    totalWidth: number
    totalHeight: number
    color: string
    index: number
    isLast: boolean
    value: number
}

const CurvedFunnelLayer: React.FC<CurvedFunnelLayerProps> = ({
    topWidth,
    bottomWidth,
    height,
    y,
    totalWidth,
    color,
    index,
    value,
}) => {
    const centerX = totalWidth / 2
    const topLeft = centerX - topWidth / 2
    const topRight = centerX + topWidth / 2
    const bottomLeft = centerX - bottomWidth / 2
    const bottomRight = centerX + bottomWidth / 2

    // Control points for bezier curves (create smooth hourglass effect)
    const curveIntensity = 0.5
    const midY = y + height / 2

    // Create path with bezier curves for smooth tapered edges
    const path = `
        M ${topLeft} ${y}
        L ${topRight} ${y}
        C ${topRight + (bottomRight - topRight) * curveIntensity} ${midY},
          ${bottomRight - (bottomRight - topRight) * curveIntensity} ${midY},
          ${bottomRight} ${y + height}
        L ${bottomLeft} ${y + height}
        C ${bottomLeft + (topLeft - bottomLeft) * curveIntensity} ${midY},
          ${topLeft - (topLeft - bottomLeft) * curveIntensity} ${midY},
          ${topLeft} ${y}
        Z
    `

    const isZero = value === 0
    const patternId = `stripe-pattern-${index}`

    return (
        <>
            {isZero && (
                <defs>
                    <pattern
                        id={patternId}
                        patternUnits="userSpaceOnUse"
                        width="8"
                        height="8"
                        patternTransform="rotate(45)"
                    >
                        <rect width="8" height="8" fill="#F3F8FF" />
                        <line
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="8"
                            stroke="#E6F0FF"
                            strokeWidth="4"
                        />
                    </pattern>
                </defs>
            )}
            <path
                d={path}
                fill={isZero ? `url(#${patternId})` : color}
                className="transition-opacity hover:opacity-90"
            />
        </>
    )
}

const ConversionFunnelChart: React.FC<ConversionFunnelChartProps> = ({
    filters,
    enabledConversionRates = false,
}) => {
    const { t } = useTranslation('report')
    const chartRef = useRef<HTMLDivElement>(null)
    const [onlyNewContacts, setOnlyNewContacts] = useState(false)

    // Calculate previous period filters
    const previousFilters = useMemo(() => {
        if (!filters.gte || !filters.lte) return null

        const start = dayjs(filters.gte)
        const end = dayjs(filters.lte)
        const diffDays = end.diff(start, 'day') + 1

        return {
            ...filters,
            gte: start.subtract(diffDays, 'day').format('YYYY-MM-DD'),
            lte: start.subtract(1, 'day').format('YYYY-MM-DD'),
        }
    }, [filters])

    // Current period query
    const { data, renderState } = useQueryWithHandler(
        {
            queryKey: [
                QUERY_KEYS.REPORTS_SALE_PERFORMANCES,
                'conversion-funnel',
                filters.gte,
                filters.lte,
                filters.sales_manager_codes,
            ],
            queryFn: () => getConversionFunnel(filters),
            enabled: !!filters.gte && !!filters.lte,
        },
        {
            loadingComponent: (
                <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100 h-full">
                    <div className="flex justify-center items-center h-[300px]">
                        <Loading.CircularProgress width={32} height={32} />
                    </div>
                </div>
            ),
        }
    )

    // Previous period query
    const { data: previousData } = useQueryWithHandler(
        {
            queryKey: [
                QUERY_KEYS.REPORTS_SALE_PERFORMANCES,
                'conversion-funnel',
                previousFilters?.gte,
                previousFilters?.lte,
                previousFilters?.sales_manager_codes,
            ],
            queryFn: () => getConversionFunnel(previousFilters!),
            enabled: !!previousFilters?.gte && !!previousFilters?.lte,
        },
        {}
    )

    // Calculate growth rates
    const calculateGrowth = (current: number, previous: number | undefined) => {
        if (!previous || previous === 0) return null
        return ((current - previous) / previous) * 100
    }

    const growthRates = useMemo(() => {
        if (!data || !previousData) return null
        return {
            contacts: calculateGrowth(
                data.summary.total_new_contacts,
                previousData.summary.total_new_contacts
            ),
            presentation: calculateGrowth(
                data.summary.total_presentation,
                previousData.summary.total_presentation
            ),
            poc: calculateGrowth(
                data.summary.total_poc,
                previousData.summary.total_poc
            ),
            contract: calculateGrowth(
                data.summary.total_contract,
                previousData.summary.total_contract
            ),
            close_win: calculateGrowth(
                data.summary.total_close_win,
                previousData.summary.total_close_win
            ),
            close_lose: calculateGrowth(
                data.summary.total_close_lose,
                previousData.summary.total_close_lose
            ),
            new_contacts_to_presentation: calculateGrowth(
                data.conversion_rates.new_contacts_to_presentation,
                previousData.conversion_rates.new_contacts_to_presentation
            ),
            new_presentation_to_poc: calculateGrowth(
                data.conversion_rates.new_presentation_to_poc,
                previousData.conversion_rates.new_presentation_to_poc
            ),
            new_contacts_to_poc: calculateGrowth(
                data.conversion_rates.new_contacts_to_poc,
                previousData.conversion_rates.new_contacts_to_poc
            ),
            new_poc_to_close_win: calculateGrowth(
                data.conversion_rates.new_poc_to_close_win,
                previousData.conversion_rates.new_poc_to_close_win
            ),
            new_contacts_to_close_win: calculateGrowth(
                data.conversion_rates.new_contacts_to_close_win,
                previousData.conversion_rates.new_contacts_to_close_win
            ),
        }
    }, [data, previousData])

    // Calculate all-time conversion rates (including old contacts)
    const allTimeRates = useMemo(() => {
        if (!data) return null

        const { summary } = data
        const totalContacts =
            summary.total_new_contacts +
            summary.presentation_from_old_contacts +
            summary.poc_from_old_contacts +
            summary.close_win_from_old_contacts

        // All-time: tính trên tổng unique customers (new + old)
        const totalPresentation = summary.total_presentation
        const totalPoc = summary.total_poc
        const totalCloseWin = summary.total_close_win

        return {
            contacts_to_presentation:
                totalContacts > 0
                    ? (totalPresentation / totalContacts) * 100
                    : 0,
            presentation_to_poc:
                totalPresentation > 0
                    ? (totalPoc / totalPresentation) * 100
                    : 0,
            contacts_to_poc:
                totalContacts > 0 ? (totalPoc / totalContacts) * 100 : 0,
            poc_to_close_win:
                totalPoc > 0 ? (totalCloseWin / totalPoc) * 100 : 0,
            contacts_to_close_win:
                totalContacts > 0 ? (totalCloseWin / totalContacts) * 100 : 0,
        }
    }, [data])

    // Get current display rates based on checkbox
    const displayRates = useMemo(() => {
        if (!data) return null

        if (onlyNewContacts) {
            // New contacts only (from API)
            return {
                contacts_to_presentation:
                    data.conversion_rates.new_contacts_to_presentation,
                presentation_to_poc:
                    data.conversion_rates.new_presentation_to_poc,
                contacts_to_poc: data.conversion_rates.new_contacts_to_poc,
                poc_to_close_win: data.conversion_rates.new_poc_to_close_win,
                contacts_to_close_win:
                    data.conversion_rates.new_contacts_to_close_win,
            }
        }

        // All-time (including old contacts)
        return allTimeRates
    }, [data, onlyNewContacts, allTimeRates])

    const exportToExcel = () => {
        if (!data) return

        // Summary data - Unique Customers (cho Conversion Funnel)
        const summaryData = [
            {
                [t('metric')]: t('new_contacts'),
                [t('current_period')]: data.summary.total_new_contacts,
                [t('previous_period')]:
                    previousData?.summary.total_new_contacts ?? '-',
                [t('growth')]: growthRates?.contacts
                    ? `${growthRates.contacts >= 0 ? '+' : ''}${growthRates.contacts.toFixed(1)}%`
                    : '-',
            },
            {
                [t('metric')]: t('presentation'),
                [t('current_period')]: data.summary.total_presentation,
                [t('previous_period')]:
                    previousData?.summary.total_presentation ?? '-',
                [t('growth')]: growthRates?.presentation
                    ? `${growthRates.presentation >= 0 ? '+' : ''}${growthRates.presentation.toFixed(1)}%`
                    : '-',
            },
            {
                [t('metric')]: t('poc'),
                [t('current_period')]: data.summary.total_poc,
                [t('previous_period')]: previousData?.summary.total_poc ?? '-',
                [t('growth')]: growthRates?.poc
                    ? `${growthRates.poc >= 0 ? '+' : ''}${growthRates.poc.toFixed(1)}%`
                    : '-',
            },
            {
                [t('metric')]: t('close_win'),
                [t('current_period')]: data.summary.total_close_win,
                [t('previous_period')]:
                    previousData?.summary.total_close_win ?? '-',
                [t('growth')]: growthRates?.close_win
                    ? `${growthRates.close_win >= 0 ? '+' : ''}${growthRates.close_win.toFixed(1)}%`
                    : '-',
            },
            {
                [t('metric')]: t('close_lose'),
                [t('current_period')]: data.summary.total_close_lose,
                [t('previous_period')]:
                    previousData?.summary.total_close_lose ?? '-',
                [t('growth')]: growthRates?.close_lose
                    ? `${growthRates.close_lose >= 0 ? '+' : ''}${growthRates.close_lose.toFixed(1)}%`
                    : '-',
            },
        ]

        // Conversion rates data
        const conversionData = [
            {
                [t('conversion_type')]: t('contacts_to_presentation'),
                [t('current_period')]:
                    `${data.conversion_rates.new_contacts_to_presentation.toFixed(1)}%`,
                [t('previous_period')]: previousData
                    ? `${previousData.conversion_rates.new_contacts_to_presentation.toFixed(1)}%`
                    : '-',
                [t('growth')]: growthRates?.new_contacts_to_presentation
                    ? `${growthRates.new_contacts_to_presentation >= 0 ? '+' : ''}${growthRates.new_contacts_to_presentation.toFixed(1)}%`
                    : '-',
            },
            {
                [t('conversion_type')]: t('presentation_to_poc'),
                [t('current_period')]:
                    `${data.conversion_rates.new_presentation_to_poc.toFixed(1)}%`,
                [t('previous_period')]: previousData
                    ? `${previousData.conversion_rates.new_presentation_to_poc.toFixed(1)}%`
                    : '-',
                [t('growth')]: growthRates?.new_presentation_to_poc
                    ? `${growthRates.new_presentation_to_poc >= 0 ? '+' : ''}${growthRates.new_presentation_to_poc.toFixed(1)}%`
                    : '-',
            },
            {
                [t('conversion_type')]: t('contacts_to_poc'),
                [t('current_period')]:
                    `${data.conversion_rates.new_contacts_to_poc.toFixed(1)}%`,
                [t('previous_period')]: previousData
                    ? `${previousData.conversion_rates.new_contacts_to_poc.toFixed(1)}%`
                    : '-',
                [t('growth')]: growthRates?.new_contacts_to_poc
                    ? `${growthRates.new_contacts_to_poc >= 0 ? '+' : ''}${growthRates.new_contacts_to_poc.toFixed(1)}%`
                    : '-',
            },
            {
                [t('conversion_type')]: t('poc_to_close_win'),
                [t('current_period')]:
                    `${data.conversion_rates.new_poc_to_close_win.toFixed(1)}%`,
                [t('previous_period')]: previousData
                    ? `${previousData.conversion_rates.new_poc_to_close_win.toFixed(1)}%`
                    : '-',
                [t('growth')]: growthRates?.new_poc_to_close_win
                    ? `${growthRates.new_poc_to_close_win >= 0 ? '+' : ''}${growthRates.new_poc_to_close_win.toFixed(1)}%`
                    : '-',
            },
            {
                [t('conversion_type')]: t('contacts_to_close_win'),
                [t('current_period')]:
                    `${data.conversion_rates.new_contacts_to_close_win.toFixed(1)}%`,
                [t('previous_period')]: previousData
                    ? `${previousData.conversion_rates.new_contacts_to_close_win.toFixed(1)}%`
                    : '-',
                [t('growth')]: growthRates?.new_contacts_to_close_win
                    ? `${growthRates.new_contacts_to_close_win >= 0 ? '+' : ''}${growthRates.new_contacts_to_close_win.toFixed(1)}%`
                    : '-',
            },
        ]

        const wb = XLSX.utils.book_new()

        // Summary sheet
        const wsSummary = XLSX.utils.json_to_sheet(summaryData)
        XLSX.utils.book_append_sheet(wb, wsSummary, t('summary'))

        // Conversion rates sheet
        const wsConversion = XLSX.utils.json_to_sheet(conversionData)
        XLSX.utils.book_append_sheet(wb, wsConversion, t('conversion_rates'))

        XLSX.writeFile(
            wb,
            `conversion_funnel_${filters.gte}_${filters.lte}.xlsx`
        )
    }

    const exportToImage = async () => {
        if (!chartRef.current) return

        const canvas = await html2canvas(chartRef.current, {
            backgroundColor: '#ffffff',
            scale: 2,
        })

        const link = document.createElement('a')
        link.download = `conversion_funnel_${filters.gte}_${filters.lte}.png`
        link.href = canvas.toDataURL('image/png')
        link.click()
    }

    const renderGrowthIndicator = (rate: number | null, small = false) => {
        if (rate === null) return null

        const iconSize = small ? 12 : 14
        const textClass = small ? 'text-[10px]' : 'text-xs'

        if (rate > 0) {
            return (
                <div className="flex items-center gap-0.5 text-green-600">
                    <TrendingUp size={iconSize} />
                    <span className={`${textClass} font-medium`}>
                        +{rate.toFixed(1)}%
                    </span>
                </div>
            )
        } else if (rate < 0) {
            return (
                <div className="flex items-center gap-0.5 text-red-600">
                    <TrendingDown size={iconSize} />
                    <span className={`${textClass} font-medium`}>
                        {rate.toFixed(1)}%
                    </span>
                </div>
            )
        }
        return (
            <div className="flex items-center gap-0.5 text-gray-500">
                <Minus size={iconSize} />
                <span className={`${textClass} font-medium`}>0%</span>
            </div>
        )
    }

    // Funnel data with labels
    const funnelData = useMemo(() => {
        if (!data) return []

        return [
            {
                name: t('funnel.new_contacts'),
                value: data.summary.total_new_contacts,
            },
            {
                name: t('funnel.presentation'),
                value: data.summary.total_presentation,
            },
            {
                name: t('funnel.poc'),
                value: data.summary.total_poc,
            },
            {
                name: t('funnel.close_win'),
                value: data.summary.total_close_win,
            },
            {
                name: t('funnel.close_lost'),
                value: data.summary.total_close_lose,
            },
        ]
    }, [data, t])

    // Funnel configuration - width proportional to actual values
    const funnelConfig = useMemo(() => {
        const svgWidth = 400
        const svgHeight = 380
        const layerCount = funnelData.length
        const layerHeight = svgHeight / layerCount
        const maxWidth = 380
        const minWidth = 80 // Minimum width for visibility

        // Find max value for scaling
        const maxValue = Math.max(...funnelData.map((d) => d.value), 1)

        // Light blue gradient colors
        const colors = ['#C1DBFF', '#CDE2FF', '#D3E6FF', '#E6F0FF', '#EFF6FF']

        // Calculate widths based on actual values
        const widths = funnelData.map((item, index) => {
            if (item.value === 0) {
                // For zero values, use a width based on position to maintain funnel shape
                const baseRatio = 0.3 - index * 0.05 // Decreasing ratio for lower layers
                return (
                    minWidth + (maxWidth - minWidth) * Math.max(baseRatio, 0.1)
                )
            }
            const ratio = item.value / maxValue
            return minWidth + (maxWidth - minWidth) * ratio
        })

        return funnelData.map((item, index) => ({
            ...item,
            topWidth: widths[index],
            bottomWidth: widths[index + 1] ?? widths[index] * 0.8,
            height: layerHeight,
            y: index * layerHeight,
            totalWidth: svgWidth,
            totalHeight: svgHeight,
            color: colors[index] ?? '#F3F8FF',
        }))
    }, [funnelData])

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    if (!data) {
        return (
            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100 h-full">
                <div className="flex justify-center items-center h-[300px] text-gray-500">
                    {t('no_data_available')}
                </div>
            </div>
        )
    }

    return (
        <div
            ref={chartRef}
            className="bg-white rounded-md p-4 shadow-sm border border-gray-100 h-full"
        >
            <div className="mb-4 flex items-start justify-between">
                <div className="flex flex-col gap-1">
                    <Typogaphy className="font-semibold text-primary">
                        {t('conversion_funnel')}
                    </Typogaphy>
                    <Typogaphy className="text-xs text-gray-500">
                        {t('b2b_sales_pipeline_overview')}{' '}
                        {t('conversion_funnel.time_range', {
                            gte: filters?.gte
                                ? dayjs(filters?.gte).format('DD/MM/YYYY')
                                : '',
                            lte: filters?.lte
                                ? dayjs(filters?.lte).format('DD/MM/YYYY')
                                : '',
                        })}
                    </Typogaphy>
                </div>
                <div className="flex gap-1">
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={exportToImage}
                        className="!min-w-0 !px-2"
                        title={t('download_image')}
                    >
                        <ImageDown size={16} />
                    </Button>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={exportToExcel}
                        className="!min-w-0 !px-2"
                        title={t('download_excel')}
                    >
                        <Download size={16} />
                    </Button>
                </div>
            </div>

            {/* Curved Funnel Chart with labels on right */}
            <div className="flex items-center justify-center">
                {/* Funnel SVG - larger */}
                <div className="flex-1 flex justify-center">
                    <svg
                        width="100%"
                        height="400"
                        viewBox="0 0 400 380"
                        className="overflow-visible max-w-[450px]"
                    >
                        {funnelConfig.map((layer, index) => (
                            <CurvedFunnelLayer
                                key={index}
                                index={index}
                                topWidth={layer.topWidth}
                                bottomWidth={layer.bottomWidth}
                                height={layer.height}
                                y={layer.y}
                                totalWidth={layer.totalWidth}
                                totalHeight={layer.totalHeight}
                                color={layer.color}
                                isLast={index === funnelConfig.length - 1}
                                value={layer.value}
                            />
                        ))}
                    </svg>
                </div>

                {/* Labels on right side - compact */}
                <div className="flex flex-col justify-between h-[400px] py-4 w-[140px] flex-shrink-0">
                    {funnelData.map((item, index) => (
                        <div key={index} className="flex flex-col">
                            <Typogaphy className="text-xs text-gray-500">
                                {item.name}
                            </Typogaphy>
                            <Typogaphy
                                className={`text-2xl font-bold ${item.value === 0 ? 'text-gray-400' : 'text-[#1a237e]'}`}
                            >
                                {item.value === 0
                                    ? '-'
                                    : item.value.toLocaleString()}
                            </Typogaphy>
                        </div>
                    ))}
                </div>
            </div>

            {/* Summary Stats */}
            <div
                className="mt-4 pt-4 border-t border-gray-100"
                data-tour="funnel-summary-stats"
            >
                <div className="grid grid-cols-5 gap-2 text-center">
                    {/* Contacts */}
                    <div
                        className="bg-blue-50 rounded-lg p-2"
                        data-tour="funnel-contacts"
                    >
                        <div className="flex items-center justify-center gap-1">
                            <Typogaphy className="text-base font-bold text-blue-600">
                                {data.summary.total_new_contacts.toLocaleString()}
                            </Typogaphy>
                            {renderGrowthIndicator(
                                growthRates?.contacts ?? null,
                                true
                            )}
                        </div>
                        <Typogaphy className="text-[10px] text-gray-500">
                            {t('new_contacts')}
                        </Typogaphy>
                        {previousData && (
                            <Typogaphy className="text-[9px] text-gray-400">
                                {t('previous')}:{' '}
                                {previousData.summary.total_new_contacts.toLocaleString()}
                            </Typogaphy>
                        )}
                    </div>

                    {/* Presentation */}
                    <div
                        className="bg-blue-50 rounded-lg p-2"
                        data-tour="funnel-presentation"
                    >
                        <div className="flex items-center justify-center gap-1">
                            <Typogaphy className="text-base font-bold text-blue-600">
                                {data.summary.total_presentation.toLocaleString()}
                            </Typogaphy>
                            {renderGrowthIndicator(
                                growthRates?.presentation ?? null,
                                true
                            )}
                        </div>
                        <Typogaphy className="text-[10px] text-gray-500">
                            {t('presentation')}
                        </Typogaphy>
                        {previousData && (
                            <Typogaphy className="text-[9px] text-gray-400">
                                {t('previous')}:{' '}
                                {previousData.summary.total_presentation.toLocaleString()}
                            </Typogaphy>
                        )}
                    </div>

                    {/* POC */}
                    <div
                        className="bg-blue-50 rounded-lg p-2"
                        data-tour="funnel-poc"
                    >
                        <div className="flex items-center justify-center gap-1">
                            <Typogaphy className="text-base font-bold text-blue-600">
                                {data.summary.total_poc.toLocaleString()}
                            </Typogaphy>
                            {renderGrowthIndicator(
                                growthRates?.poc ?? null,
                                true
                            )}
                        </div>
                        <Typogaphy className="text-[10px] text-gray-500">
                            {t('poc')}
                        </Typogaphy>
                        {previousData && (
                            <Typogaphy className="text-[9px] text-gray-400">
                                {t('previous')}:{' '}
                                {previousData.summary.total_poc.toLocaleString()}
                            </Typogaphy>
                        )}
                    </div>

                    {/* Close Win */}
                    <div
                        className="bg-green-50 rounded-lg p-2"
                        data-tour="funnel-close-win"
                    >
                        <div className="flex items-center justify-center gap-1">
                            <Typogaphy className="text-base font-bold text-green-600">
                                {data.summary.total_close_win.toLocaleString()}
                            </Typogaphy>
                            {renderGrowthIndicator(
                                growthRates?.close_win ?? null,
                                true
                            )}
                        </div>
                        <Typogaphy className="text-[10px] text-gray-500">
                            {t('close_win')}
                        </Typogaphy>
                        {previousData && (
                            <Typogaphy className="text-[9px] text-gray-400">
                                {t('previous')}:{' '}
                                {previousData.summary.total_close_win.toLocaleString()}
                            </Typogaphy>
                        )}
                    </div>

                    {/* Close Lose */}
                    <div
                        className="bg-red-50 rounded-lg p-2"
                        data-tour="funnel-close-lose"
                    >
                        <div className="flex items-center justify-center gap-1">
                            <Typogaphy className="text-base font-bold text-red-600">
                                {data.summary.total_close_lose.toLocaleString()}
                            </Typogaphy>
                            {renderGrowthIndicator(
                                growthRates?.close_lose ?? null,
                                true
                            )}
                        </div>
                        <Typogaphy className="text-[10px] text-gray-500">
                            {t('close_lose')}
                        </Typogaphy>
                        {previousData && (
                            <Typogaphy className="text-[9px] text-gray-400">
                                {t('previous')}:{' '}
                                {previousData.summary.total_close_lose.toLocaleString()}
                            </Typogaphy>
                        )}
                    </div>
                </div>
            </div>

            {/* Conversion Rates - Chỉ cho contacts mới */}
            {enabledConversionRates && (
                <div className="mt-4">
                    <div className="flex mb-2 flex-col gap-1">
                        <Typogaphy className="text-xs font-medium text-gray-700">
                            {t('conversion_rates')}
                        </Typogaphy>
                        <label className="flex items-center gap-1.5 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={onlyNewContacts}
                                onChange={(e) =>
                                    setOnlyNewContacts(e.target.checked)
                                }
                                className="w-3.5 h-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <Typogaphy className="text-[10px] text-gray-600">
                                {t('new_contacts_only', {
                                    gte: filters?.gte ?? '',
                                    lte: filters?.lte ?? '',
                                })}
                            </Typogaphy>
                        </label>
                    </div>

                    <div className="grid grid-cols-5 gap-2">
                        <div className="bg-amber-50 rounded-lg p-2 text-center">
                            <div className="flex items-center justify-center gap-1">
                                <Typogaphy className="text-base font-bold text-amber-600">
                                    {displayRates?.contacts_to_presentation.toFixed(
                                        1
                                    )}
                                    %
                                </Typogaphy>
                                {onlyNewContacts &&
                                    renderGrowthIndicator(
                                        growthRates?.new_contacts_to_presentation ??
                                            null,
                                        true
                                    )}
                            </div>
                            <Typogaphy className="text-[10px] text-gray-600 leading-tight">
                                {t('contacts_to_presentation')}
                            </Typogaphy>
                        </div>

                        <div className="bg-lime-50 rounded-lg p-2 text-center">
                            <div className="flex items-center justify-center gap-1">
                                <Typogaphy className="text-base font-bold text-lime-600">
                                    {displayRates?.presentation_to_poc.toFixed(
                                        1
                                    )}
                                    %
                                </Typogaphy>
                                {onlyNewContacts &&
                                    renderGrowthIndicator(
                                        growthRates?.new_presentation_to_poc ??
                                            null,
                                        true
                                    )}
                            </div>
                            <Typogaphy className="text-[10px] text-gray-600 leading-tight">
                                {t('presentation_to_poc')}
                            </Typogaphy>
                        </div>

                        <div className="bg-gray-100 rounded-lg p-2 text-center">
                            <div className="flex items-center justify-center gap-1">
                                <Typogaphy className="text-base font-bold text-gray-700">
                                    {displayRates?.contacts_to_poc.toFixed(1)}%
                                </Typogaphy>
                                {onlyNewContacts &&
                                    renderGrowthIndicator(
                                        growthRates?.new_contacts_to_poc ??
                                            null,
                                        true
                                    )}
                            </div>
                            <Typogaphy className="text-[10px] text-gray-600 leading-tight">
                                {t('contacts_to_poc')}
                            </Typogaphy>
                        </div>

                        <div className="bg-cyan-50 rounded-lg p-2 text-center">
                            <div className="flex items-center justify-center gap-1">
                                <Typogaphy className="text-base font-bold text-cyan-600">
                                    {displayRates?.poc_to_close_win.toFixed(1)}%
                                </Typogaphy>
                                {onlyNewContacts &&
                                    renderGrowthIndicator(
                                        growthRates?.new_poc_to_close_win ??
                                            null,
                                        true
                                    )}
                            </div>
                            <Typogaphy className="text-[10px] text-gray-600 leading-tight">
                                {t('poc_to_close_win')}
                            </Typogaphy>
                        </div>

                        <div className="bg-emerald-50 rounded-lg p-2 text-center">
                            <div className="flex items-center justify-center gap-1">
                                <Typogaphy className="text-base font-bold text-emerald-600">
                                    {displayRates?.contacts_to_close_win.toFixed(
                                        1
                                    )}
                                    %
                                </Typogaphy>
                                {onlyNewContacts &&
                                    renderGrowthIndicator(
                                        growthRates?.new_contacts_to_close_win ??
                                            null,
                                        true
                                    )}
                            </div>
                            <Typogaphy className="text-[10px] text-gray-600 leading-tight">
                                {t('contacts_to_close_win')}
                            </Typogaphy>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default ConversionFunnelChart
